/*
 * cart-mutation.js — HCSCart: the single seam for changing the cart.
 * ------------------------------------------------------------------
 * The theme already ships Dawn's pub/sub (pubsub.js + constants.js) and the
 * cart core (cart-v2.js / cart-drawer-v2.js) already publishes/subscribes to
 * PUB_SUB_EVENTS.cartUpdate to re-render the drawer, icon bubble and /cart page.
 *
 * The problem this fixes: several inline snippet scripts (frozen bundle, upsell
 * cards, empty-cart card, timer bar) bypassed that pub/sub and hand-rolled their
 * own add + manual drawer re-fetch — duplicating the re-render the core already
 * owns, and re-introducing the "bare /cart/add.js updates the server cart but not
 * Dawn's drawer" bug (see the old comment in frozen-bundle-block.liquid).
 *
 * HCSCart wraps the EXISTING mechanism. It does NOT replace cart-v2.js. It POSTs
 * the mutation, drives the proven fast-path render on the <cart-drawer> element
 * (renderContents, which also opens the drawer), then publishes cartUpdate so
 * every other subscriber (timer bar, etc.) reacts. One place owns "mutate → render
 * → notify"; callers just say HCSCart.add(...) / HCSCart.subscribe(...).
 *
 * Loaded after cart-v2.js (see sections/cart-drawer.liquid), so window.publish,
 * window.PUB_SUB_EVENTS and the <cart-drawer> element are available.
 */
(function () {
  'use strict';

  function routeBase() {
    // window.routes is set by Dawn's theme.liquid; fall back to defaults.
    return (window.routes || (window.Shopify && window.Shopify.routes) || {});
  }

  function addUrl() {
    var r = routeBase();
    return (r.cart_add_url || '/cart/add') + '.js';
  }

  function changeUrl() {
    var r = routeBase();
    return (r.cart_change_url || '/cart/change') + '.js';
  }

  function updateUrl() {
    var r = routeBase();
    return (r.cart_update_url || '/cart/update') + '.js';
  }

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  // Section ids the drawer needs in the Section Rendering API response so the
  // fast-path renderContents() can patch them. Read from the live element so we
  // never duplicate the "which sections" list — it lives in cart-drawer-v2.js.
  function drawerSections(el) {
    if (el && typeof el.getSectionsToRender === 'function') {
      try {
        return el.getSectionsToRender()
          .map(function (s) { return s.id || s.section; })
          .filter(Boolean);
      } catch (e) { /* fall through */ }
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          // Shopify returns {status, message, description} on add errors.
          var err = new Error((data && (data.description || data.message)) || 'Cart request failed');
          err.cartError = data;
          throw err;
        }
        return data;
      });
    });
  }

  // Publish the standard cartUpdate event. When we've already rendered the drawer
  // ourselves (rendered=true), use source "cart-items" so the core drawer/cart
  // subscribers IGNORE it (no double render); other subscribers (timer bar) still
  // react. When we did NOT render (no drawer on the page, e.g. /cart), use a
  // non-"cart-items" source so the core subscribers DO re-fetch and re-render.
  function announce(cartData, variantId, rendered) {
    if (!window.publish || !window.PUB_SUB_EVENTS) return;
    window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
      source: rendered ? 'cart-items' : 'hcs-cart-mutation',
      cartData: cartData,
      variantId: variantId != null ? variantId : (cartData && cartData.id)
    });
  }

  // Drive the proven fast-path render on the drawer (updates sections + opens).
  // Returns true if it rendered.
  function renderDrawer(el, data, openDrawer) {
    if (!el || !data || !data.sections || typeof el.renderContents !== 'function') return false;
    var restore;
    if (openDrawer === false && typeof el.open === 'function') {
      // Temporarily neutralize auto-open (renderContents opens the drawer).
      restore = el.open;
      el.open = function () {};
    }
    try {
      el.renderContents(data);
    } catch (e) {
      if (restore) el.open = restore;
      return false;
    }
    if (restore) {
      setTimeout(function () { el.open = restore; }, 300);
    }
    return true;
  }

  // Normalize add input: accept ({id, quantity}), ({items:[...]}), or (variantId).
  function normalizeAdd(input) {
    if (typeof input === 'number' || typeof input === 'string') {
      return { items: [{ id: input, quantity: 1 }] };
    }
    if (input && input.items) return { items: input.items };
    if (input && input.id != null) {
      return { items: [{ id: input.id, quantity: input.quantity || 1 }] };
    }
    return { items: [] };
  }

  function firstVariantId(body) {
    return body && body.items && body.items[0] ? body.items[0].id : undefined;
  }

  var HCSCart = {
    /*
     * add(input, opts) — add line item(s) to the cart.
     *   input: variantId | {id, quantity} | {items:[{id, quantity, properties}]}
     *   opts:  { openDrawer?: boolean (default true), sectionsUrl?: string }
     * Resolves with the /cart/add.js JSON (full cart).
     */
    add: function (input, opts) {
      opts = opts || {};
      var el = drawerEl();
      var body = normalizeAdd(input);
      if (el) {
        body.sections = drawerSections(el);
        body.sections_url = opts.sectionsUrl || window.location.pathname;
      }
      var variantId = firstVariantId(body);
      return post(addUrl(), body).then(function (data) {
        var rendered = renderDrawer(el, data, opts.openDrawer !== false);
        announce(data, variantId, rendered);
        return data;
      });
    },

    /*
     * change(args) — change a line's quantity / properties.
     *   args: { line?, id?, quantity, properties? }  (Shopify /cart/change.js)
     * Resolves with the full cart JSON.
     */
    change: function (args) {
      args = args || {};
      var el = drawerEl();
      var body = {};
      if (args.id != null) body.id = args.id;
      if (args.line != null) body.line = args.line;
      if (args.quantity != null) body.quantity = args.quantity;
      if (args.properties) body.properties = args.properties;
      if (el) {
        body.sections = drawerSections(el);
        body.sections_url = window.location.pathname;
      }
      return post(changeUrl(), body).then(function (data) {
        var rendered = renderDrawer(el, data, args.openDrawer === true);
        announce(data, args.variantId, rendered);
        return data;
      });
    },

    /*
     * update(body) — /cart/update.js (attributes, note, etc.).
     * Resolves with the full cart JSON.
     */
    update: function (body) {
      body = body || {};
      var el = drawerEl();
      if (el) {
        body.sections = drawerSections(el);
        body.sections_url = window.location.pathname;
      }
      return post(updateUrl(), body).then(function (data) {
        var rendered = renderDrawer(el, data, false);
        announce(data, undefined, rendered);
        return data;
      });
    },

    /*
     * subscribe(fn) — run fn(eventPayload) after every cart change.
     * Returns an unsubscribe function. Thin wrapper over the existing pub/sub,
     * so it catches changes from the core (qty/remove) AND from HCSCart.
     */
    subscribe: function (fn) {
      if (!window.subscribe || !window.PUB_SUB_EVENTS) return function () {};
      return window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, fn);
    },

    /* get() — fetch current cart JSON (no mutation). */
    get: function () {
      var r = routeBase();
      return fetch((r.cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); });
    }
  };

  window.HCSCart = HCSCart;
})();
